// HelpCenterPage — Extensive help center with categories + search
// Layout:
//   - Top nav: < Back | Help
//   - Search bar
//   - Quick action cards (Contact, Docs, Status, Community)
//   - Category list
//   - Popular articles

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Article {
  id: string;
  category: string;
  title: string;
  body: string;
  helpful: number;
}

const CATEGORIES = [
  { id: 'getting-started', label: 'Getting started', icon: '🚀', count: 8 },
  { id: 'challenges',      label: 'Challenges',       icon: '🎯', count: 14 },
  { id: 'platforms',       label: 'Platforms',        icon: '💻', count: 11 },
  { id: 'ai-bot',          label: 'AI Bot Trader',    icon: '🤖', count: 9 },
  { id: 'payments',        label: 'Payments',         icon: '💳', count: 7 },
  { id: 'kyc',             label: 'KYC & Verification', icon: '🆔', count: 4 },
  { id: 'account',         label: 'Account',          icon: '👤', count: 6 },
  { id: 'compliance',      label: 'Rules & Compliance', icon: '📋', count: 5 },
];

const POPULAR: Article[] = [
  { id: 'a1', category: 'Getting started', title: 'How do I activate my first challenge?',
    body: '1. Go to the Discover tab. 2. Pick a trading platform. 3. Choose tier and size. 4. Pay to activate.',
    helpful: 245 },
  { id: 'a2', category: 'AI Bot Trader', title: 'How does the AI Bot Trader work?',
    body: 'The bot connects to your active challenge via the platform license. It runs the strategy you picked and enters/exits based on real-time market signals.',
    helpful: 198 },
  { id: 'a3', category: 'Challenges', title: 'What happens if I breach the daily loss limit?',
    body: 'Your challenge is automatically failed. You can reset the challenge for a reset fee (varies by size).',
    helpful: 167 },
  { id: 'a4', category: 'Payments', title: 'When can I withdraw my first payout?',
    body: 'After 5 funded days (8 for Pro tier) with a minimum of $100 in profit. Payouts are processed within 1-3 business days.',
    helpful: 142 },
  { id: 'a5', category: 'Platforms', title: 'Can I switch platforms mid-challenge?',
    body: 'Yes, you can switch active platforms once per challenge. Future challenges can be on any platform your tier allows.',
    helpful: 89 },
  { id: 'a6', category: 'Rules', title: 'What is EOD Balance drawdown?',
    body: 'End-of-day balance drawdown. Your account is evaluated at market close. If equity falls below (initial balance - max drawdown), the challenge fails.',
    helpful: 78 },
];

export function HelpCenterPage() {
  const nav = useNavigate();
  const [q, setQ] = useState('');

  const filtered = POPULAR.filter(a => !q || a.title.toLowerCase().includes(q.toLowerCase()) || a.body.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Help center</div>
        <div className="w-9" />
      </div>

      {/* Search */}
      <div className="px-4 pt-4">
        <div className="flex items-center gap-2 rounded-[10px] bg-tf-surface-1 px-3 py-2">
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles"
            data-testid="help-search"
            className="flex-1 bg-transparent text-[15px] text-tf-text-1 outline-none placeholder:text-tf-text-3"
          />
        </div>
      </div>

      {/* Quick actions */}
      {q === '' && (
        <div className="mx-4 mt-4 grid grid-cols-2 gap-2">
          <QuickAction icon="💬" label="Live chat"     sub="Avg 2 min" onClick={() => alert('Open chat')} />
          <QuickAction icon="📧" label="Email us"      sub="24h reply" onClick={() => window.location.href = 'mailto:support@funderpro.live'} />
          <QuickAction icon="📊" label="Status"        sub="All systems normal" onClick={() => window.open('https://status.funderpro.live', '_blank')} />
          <QuickAction icon="👥" label="Community"     sub="Discord · 12k traders" onClick={() => window.open('https://discord.gg/eqFEg3QpcE', '_blank')} />
        </div>
      )}

      {/* Categories */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Categories</h2>
      <div className="mx-4 grid grid-cols-2 gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => alert('Open ' + c.label)}
            data-testid={`help-cat-${c.id}`}
            className="flex items-center gap-3 rounded-[14px] border border-tf-border bg-white p-3 text-left active:scale-[0.99]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[20px]">{c.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1">{c.label}</div>
              <div className="text-[11px] text-tf-text-3">{c.count} articles</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ))}
      </div>

      {/* Popular articles */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">
        {q ? `Results for "${q}"` : 'Popular articles'}
      </h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-[14px] text-tf-text-3">No articles found</div>
        ) : filtered.map((a, i) => (
          <button
            key={a.id}
            onClick={() => alert('Open article: ' + a.title)}
            className={['flex w-full items-start gap-3 border-tf-border px-4 py-3 text-left active:bg-tf-surface-1', i < filtered.length - 1 ? 'border-b' : ''].join(' ')}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tf-surface-1 text-tf-text-2 text-[16px]">📄</div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1">{a.title}</div>
              <div className="line-clamp-2 text-[12px] text-tf-text-3">{a.body}</div>
              <div className="mt-1 text-[10px] text-tf-text-3">{a.helpful} found this helpful</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ))}
      </div>

      {/* Contact CTA */}
      <div className="mx-4 mt-5 rounded-[16px] border border-tf-border bg-white p-4 text-center">
        <div className="text-[16px] font-semibold text-tf-text-1">Still need help?</div>
        <div className="mt-1 text-[13px] text-tf-text-3">Our support team is available 24/7.</div>
        <button onClick={() => alert('Open chat')} className="mt-3 inline-flex h-[40px] items-center justify-center rounded-full bg-tf-text-1 px-6 text-[14px] font-semibold text-white active:scale-[0.99]">
          Start live chat
        </button>
      </div>
    </div>
  );
}

function QuickAction({ icon, label, sub, onClick }: { icon: string; label: string; sub: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-[14px] border border-tf-border bg-white p-3 text-left active:scale-[0.99]">
      <div className="text-[24px]">{icon}</div>
      <div className="mt-2 text-[14px] font-semibold text-tf-text-1">{label}</div>
      <div className="text-[11px] text-tf-text-3">{sub}</div>
    </button>
  );
}
